import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import { usePermissions, Permissions } from '../../contexts/PermissionContext';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageArea from '../shared/PageArea';
import PagedItems from '../shared/PagedItems';
import LoadingWheel from '../shared/LoadingWheel';
import ProjectConflictsPanel from './ProjectConflictsPanel';
import ProjectStakeholdersPanel from './ProjectStakeholdersPanel';
import { StatusChip, confidenceTone, documentStatusTone, fieldStatusTone, formatConfidence, projectStatusTone } from '../shared/StatusChip';
import { DocumentsClient, ProcessingClient, ProjectDocumentItem, ProjectFieldItem, ProjectsClient, ProjectSummaryResponse } from '../../web-api-client';
import { authenticatedFetch, getApiBaseUrl } from '../../api/apiConfig';

const instantAccordionSx = {
  transition: 'none',
  '& .MuiAccordionSummary-root, & .MuiAccordionSummary-content, & .MuiAccordionSummary-expandIconWrapper': { transition: 'none' },
};

const ProjectSummaryPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteReference, setDeleteReference] = useState('');
  const [deleting, setDeleting] = useState(false);
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const tab = search.get('tab') ?? 'overview';
  const projectsClient = useMemo(() => new ProjectsClient(getApiBaseUrl(), authenticatedFetch), []);
  const processingClient = useMemo(() => new ProcessingClient(getApiBaseUrl(), authenticatedFetch), []);
  const documentsClient = useMemo(() => new DocumentsClient(getApiBaseUrl(), authenticatedFetch), []);

  const [summary, setSummary] = useState<ProjectSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [documentSearch, setDocumentSearch] = useState('');
  const [confirmBulkVerify, setConfirmBulkVerify] = useState(false);

  const load = useCallback(() => {
    if (!projectId) {
      return;
    }

    setLoading(true);
    projectsClient
      .getApiProjectsSummary(projectId)
      .then(setSummary)
      .catch(() => setSummary(null))
      .finally(() => setLoading(false));
  }, [projectsClient, projectId]);

  useEffect(() => load(), [load]);

  const bulkVerify = async () => {
    if (!projectId) {
      return;
    }

    try {
      const result = await processingClient.postApiFieldsBulkVerify({ projectId });
      setMessage(t('projectSummary.verified', { count: result.verifiedCount ?? 0 }));
      load();
    } catch {
      setMessage(t('common.error'));
    }
  };

  const deleteProject = async () => {
    if (!projectId) return;
    setDeleting(true);
    try {
      const response = await authenticatedFetch.fetch(`${getApiBaseUrl() ?? ''}/api/projects/${encodeURIComponent(projectId)}`, { method: 'DELETE' });
      if (!response.ok) {
        const problem = await response.json();
        throw new Error(problem.detail ?? problem.title ?? t('common.error'));
      }
      navigate('/projects', { replace: true });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : t('common.error'));
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  const downloadZip = async () => {
    if (!projectId) {
      return;
    }

    const response = await authenticatedFetch.fetch(`${getApiBaseUrl() ?? ''}/api/projects/${projectId}/documents/zip`);
    if (response.ok) {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${summary?.internalReference ?? 'project'}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  };

  const downloadDocument = async (documentId: string) => {
    try {
      const content = await documentsClient.getApiDocumentsContent(documentId);
      if (content.downloadUrl) {
        window.open(content.downloadUrl, '_blank', 'noopener,noreferrer');
      }
    } catch {
      setMessage(t('common.error'));
    }
  };

  const documentGroups = useMemo(() => tab === 'documents' ? groupDocuments(summary?.documents ?? [], documentSearch) : [], [summary?.documents, documentSearch, tab]);
  const documentNames = useMemo(() => new Map(summary?.documents?.map(d => [d.documentId, d.fileName])), [summary?.documents]);

  if (loading) {
    return (
      <PageArea>
        <LoadingWheel />
      </PageArea>
    );
  }

  if (!summary) {
    return (
      <PageArea>
        <Typography color="text.secondary">{t('common.noData')}</Typography>
      </PageArea>
    );
  }

  const verifiedCount = (summary.extractedFields ?? []).filter((f) => f.status === 'Verified' || f.status === 'Corrected').length;
  const reviewCount = (summary.extractedFields ?? []).filter((f) => f.status === 'NeedsReview').length;

  return (
    <PageArea>
      <Button size="md" startIcon={<ArrowBackIcon />} onClick={() => navigate('/projects')} sx={{ mb: 1 }}>
        {t('common.projects')}
      </Button>

      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'flex-start' }} spacing={2} sx={{ pb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 20 }}>
              {[summary.projectNumber ?? summary.internalReference, summary.vesselName].filter(Boolean).join(' · ')}
            </Typography>
            <StatusChip label={summary.status ?? ''} tone={projectStatusTone(summary.status)} />
            {summary.conflictStatus && <StatusChip label={t(`projects.status.${summary.conflictStatus}`)} tone={summary.conflictStatus === 'PotentialConflict' ? 'warning' : 'default'} />}
          </Stack>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            {summary.cargoDescription}
            {summary.vesselName ? ` · ${summary.vesselName}` : ''}
            {summary.externalReference ? ` · ${summary.externalReference}` : ''}
            {summary.externalCompanyName ? ` · ${summary.externalCompanyName}` : ''}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {hasPermission(Permissions.PROJECT_MANAGE) && <Button size="md" color="error" startIcon={<DeleteIcon />}
            onClick={() => { setDeleteReference(''); setConfirmDelete(true); }}>{t('projectSummary.deleteProject')}</Button>}
          <Button size="md" component={RouterLink} to={`/upload?project=${summary.id}`} variant="secondary" startIcon={<UploadFileIcon />}>
            {t('common.upload')}
          </Button>
          <Button size="md" variant="secondary" onClick={downloadZip}>
            {t('projectSummary.downloadZip')}
          </Button>
          <Button size="md" variant="primary" onClick={() => setConfirmBulkVerify(true)}>
            {t('projectSummary.bulkVerify')}
          </Button>
        </Stack>
      </Stack>

      {message && (
        <Alert severity="info" sx={{ mt: 2 }} onClose={() => setMessage(null)}>
          {message}
        </Alert>
      )}

      <Dialog open={confirmDelete} onClose={() => !deleting && setConfirmDelete(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('projectSummary.deleteProject')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>{t('projectSummary.deleteWarning')}</Alert>
          <TextField label={t('projectSummary.deleteConfirmation', { reference: summary.internalReference })}
            value={deleteReference} onChange={e => setDeleteReference(e.target.value)} fullWidth autoComplete="off" />
        </DialogContent>
        <DialogActions>
          <Button size="md" disabled={deleting} onClick={() => setConfirmDelete(false)}>{t('common.cancel')}</Button>
          <Button size="md" color="error" variant="primary" disabled={deleting || deleteReference !== summary.internalReference}
            onClick={deleteProject}>{t('projectSummary.deleteProject')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmBulkVerify} onClose={() => setConfirmBulkVerify(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('projectSummary.confirmVerifyTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('projectSummary.confirmBulkVerify', { project: summary.internalReference })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button size="md" onClick={() => setConfirmBulkVerify(false)}>{t('common.cancel')}</Button>
          <Button
            size="md"
            variant="primary"
            onClick={() => {
              setConfirmBulkVerify(false);
              void bulkVerify();
            }}
          >
            {t('projectSummary.bulkVerify')}
          </Button>
        </DialogActions>
      </Dialog>

      {(summary.uploadPacks ?? []).filter((pack) => pack.rule !== 'PerArchiveMatching').map((pack) => (
        <Alert key={pack.batchId} severity={summary.conflictStatus === 'PotentialConflict' ? 'warning' : 'info'} sx={{ mt: 2 }}>
          {t(`groupingRules.${pack.rule}`)}
        </Alert>
      ))}

      <Tabs
        variant="scrollable"
        scrollButtons="auto"
        value={tab}
        onChange={(_, value) => setSearch({ tab: value })}
        sx={{ mt: 2, mb: 2 }}
      >
        <Tab value="overview" label={t('projectSummary.overview')} />
        <Tab value="documents" label={t('projectSummary.documents')} />
        <Tab value="extracted-data" label={t('projectSummary.extractedFields')} />
        <Tab value="conflicts" label={t('projects.conflicts')} />
        <Tab value="stakeholders" label={t('projects.stakeholders')} />
      </Tabs>

      {tab === 'overview' && (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr' }}>
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              borderRadius: 2,
              bgcolor: 'primary.25',
              borderColor: 'primary.200',
              borderLeft: 4,
              borderLeftColor: 'primary.500',
            }}
          >
            <Typography sx={{ fontWeight: 600, mb: 2, color: 'primary.800' }}>
              {t('projectSummary.cargoVoyage')}
            </Typography>
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
              <OverviewCard label={t('projectsPage.client')} value={summary.externalClientName} />
              <OverviewCard label={t('projectsPage.cargo')} value={summary.cargoDescription} />
              <OverviewCard label={t('projectsPage.vessel')} value={summary.vesselName} />
              <OverviewCard label={t('projectOverview.client_reference')} value={summary.externalReference ?? summary.overview?.find(item => item.fieldKey === 'client_reference')?.values?.map(value => value.value).join('; ')} />
              <OverviewCard label={t('projectsPage.projectNumber')} value={summary.projectNumber} />
            </Box>
          </Paper>
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              borderRadius: 2,
              width: '100%',
              boxSizing: 'border-box',
              bgcolor: 'custom.violet.25',
              borderColor: 'custom.violet.200',
              borderLeft: 4,
              borderLeftColor: 'custom.violet.500',
            }}
          >
            <Typography sx={{ fontWeight: 600, mb: 1, color: 'custom.violet.800' }}>
              {t('projectOverview.title')}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>{t('projectOverview.hint')}</Typography>
            <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
              {(summary.overview ?? []).map(item => (
                <Box
                  key={item.fieldKey}
                  sx={{ p: 2.5, borderRadius: 2, bgcolor: 'common.white', border: 1, borderColor: 'custom.violet.100' }}
                >
                  <Typography sx={{ color: 'custom.violet.700', fontWeight: 600, mb: 1 }}>
                    {t(`projectOverview.${item.fieldKey}`)}
                  </Typography>
                  <Stack spacing={4}>
                    <PagedItems items={item.values ?? []}>{values => values.map(value => (
                      <Box key={value.value}>
                        <Typography sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{value.value}</Typography>
                        <Stack direction="row" useFlexGap flexWrap="wrap" spacing={4} sx={{ mt: 4 }}>
                          <PagedItems items={value.documentIds ?? []}>{ids => ids.map(id => (
                            <Button size="md" variant="secondary" key={id} component={RouterLink} to={`/review/${id}`} sx={{ textAlign: 'left', maxWidth: '100%', whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                              {documentNames.get(id) ?? t('projectSummary.review')}
                          </Button>
                        ))}</PagedItems>
                        </Stack>
                      </Box>
                    ))}</PagedItems>
                  </Stack>
                </Box>
              ))}
            </Box>
          </Paper>
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              borderRadius: 2,
              bgcolor: 'success.25',
              borderColor: 'success.200',
              borderLeft: 4,
              borderLeftColor: 'success.500',
            }}
          >
            <Typography sx={{ fontWeight: 600, mb: 2, color: 'success.800' }}>
              {t('projectSummary.dataHealth')}
            </Typography>
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr 1fr' }}>
              <OverviewCard label={t('projectSummary.verifiedFields')} value={String(verifiedCount)} />
              <OverviewCard label={t('projectSummary.needReview')} value={String(reviewCount)} />
              <OverviewCard label={t('projectSummary.documents')} value={String(summary.documents?.length ?? 0)} />
              <OverviewCard label={t('projectSummary.extractedFields')} value={String(summary.extractedFields?.length ?? 0)} />
            </Box>
          </Paper>
        </Box>
      )}

      {tab === 'documents' && (
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1.5}>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>{t('projectDocuments.title')}</Typography>
              <Typography color="text.secondary">{t('projectDocuments.hint')}</Typography>
            </Box>
            <TextField
              size="small"
              value={documentSearch}
              onChange={(event) => setDocumentSearch(event.target.value)}
              placeholder={t('projectDocuments.search')}
              inputProps={{ 'aria-label': t('projectDocuments.search') }}
              sx={{ width: { xs: '100%', sm: 320 } }}
            />
          </Stack>
          {documentGroups.map((group) => (
            <Accordion
              key={group.key}
              TransitionProps={{ timeout: 0, unmountOnExit: true }}
              disableGutters
              sx={{
                border: 1,
                ...instantAccordionSx,
                borderColor: group.border,
                borderLeft: 4,
                borderLeftColor: group.accent,
                borderRadius: '8px !important',
                bgcolor: group.background,
                '&::before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2.5, minHeight: 64 }}>
                <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                  <Typography sx={{ fontWeight: 600, color: group.heading }}>{t(`projectDocuments.groups.${group.key}`)}</Typography>
                  <StatusChip label={t('projectDocuments.count', { count: group.documents.length })} />
                  {group.reviewCount > 0 && <StatusChip label={t('projectSummary.toReview', { count: group.reviewCount })} tone="warning" />}
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0, bgcolor: 'common.white', borderRadius: '0 0 8px 8px', overflowX: 'auto' }}>
                <DocumentTable documents={group.documents} onReview={(id) => navigate(`/review/${id}`)} onDownload={downloadDocument} />
              </AccordionDetails>
            </Accordion>
          ))}
          {documentGroups.length === 0 && (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
              <Typography color="text.secondary">{t('projectDocuments.noMatches')}</Typography>
            </Paper>
          )}
        </Stack>
      )}

      {tab === 'conflicts' && projectId && <ProjectConflictsPanel projectId={projectId} projectReference={summary.internalReference ?? ''} documents={summary.documents ?? []} onResolved={load} />}
      {tab === 'stakeholders' && projectId && <ProjectStakeholdersPanel projectId={projectId} />}

      {tab === 'extracted-data' && (
        <ExtractedDataPanel summary={summary} onReview={(id) => navigate(`/review/${id}`)} />
      )}
    </PageArea>
  );
};

const OverviewCard: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
    <Typography variant="textSM" color="text.secondary">
      {label}
    </Typography>
    <Typography sx={{ fontWeight: 600 }}>{value || '-'}</Typography>
  </Paper>
);

type DocumentGroup = {
  key: string;
  documents: ProjectDocumentItem[];
  reviewCount: number;
  background: string;
  border: string;
  accent: string;
  heading: string;
};

const documentGroupOrder = ['quality', 'shipping', 'operations', 'visual', 'other'];

const documentGroupCode: Record<string, string> = {
  MOISTURE_CONTENT_CERTIFICATE: 'quality',
  TRANSPORTABLE_MOISTURE_LIMIT_CERTIFICATE_TML: 'quality',
  PARTICLE_SIZE_DISTRIBUTION_CERTIFICATE_PSD: 'quality',
  'X-RAY_FLUORESCENCE_CERTIFICATE_XRD': 'quality',
  'X-RAY_DIFFRACTION_CERTIFICATE_XRD': 'quality',
  PARTICLE_DENSITY_CERTIFICATE_PD: 'quality',
  CAN_TEST_REPORT: 'quality',
  CARGO_SPECIFICATION: 'quality',
  ANALYSIS_CERTIFICATE_GENERAL: 'quality',
  LABORATORY_AUDIT_REPORT: 'quality',
  CALIBRATION_REPORT: 'quality',
  SHIPPERS_DECLARATION: 'shipping',
  CARGO_MANIFEST: 'shipping',
  BILL_OF_LADING: 'shipping',
  CHARTER_PARTY: 'shipping',
  STOWAGE_PLAN: 'shipping',
  STABILITY_BOOK: 'shipping',
  IMSBC_CODE_SCHEDULE: 'shipping',
  MSDS: 'shipping',
  FUMIGATION_CERTIFICATE: 'shipping',
  PHYTOSANITARY_CERTIFICATE: 'shipping',
  SAMPLING_REPORT: 'operations',
  HATCH_COVER_REPORT: 'operations',
  DRAFT_SURVEY: 'operations',
  BILGE_REPORT: 'operations',
  VENTILATION_RECORDS: 'operations',
  WEATHER_DATA: 'operations',
  AIS_FILES: 'operations',
  SURVEY_REPORT: 'operations',
  'CARGO_TEMPERATURE_&_MOISTURE_REPORT': 'operations',
  STATEMENT_OF_FACTS: 'operations',
  LETTER_OF_PROTEST: 'operations',
  LOADING_REPORT: 'operations',
  DISCHARGE_REPORT: 'operations',
  PHOTOGRAPH_CARGO: 'visual',
  PHOTOGRAPH_VESSEL: 'visual',
  PHOTOGRAPH_OTHER: 'visual',
};

const documentGroupStyle: Record<string, Pick<DocumentGroup, 'background' | 'border' | 'accent' | 'heading'>> = {
  quality: { background: 'custom.blue.25', border: 'custom.blue.200', accent: 'custom.blue.500', heading: 'custom.blue.800' },
  shipping: { background: 'custom.violet.25', border: 'custom.violet.200', accent: 'custom.violet.500', heading: 'custom.violet.800' },
  operations: { background: 'success.25', border: 'success.200', accent: 'success.500', heading: 'success.800' },
  visual: { background: 'warning.25', border: 'warning.200', accent: 'warning.500', heading: 'warning.800' },
  other: { background: 'custom.gray.25', border: 'custom.gray.200', accent: 'custom.gray.500', heading: 'custom.gray.800' },
};

const groupDocuments = (documents: ProjectDocumentItem[], search: string): DocumentGroup[] => {
  const term = search.trim().toLocaleLowerCase();
  const matching = documents.filter((document) => !term || [document.fileName, document.documentTypeName, document.documentTypeCode, document.status]
    .some((value) => value?.toLocaleLowerCase().includes(term)));
  return documentGroupOrder.map((key) => {
    const grouped = matching.filter((document) => (document.isPhoto ? 'visual' : documentGroupCode[document.documentTypeCode ?? ''] ?? 'other') === key);
    return {
      key,
      documents: grouped,
      reviewCount: grouped.filter((document) => document.status === 'NeedsReview').length,
      ...documentGroupStyle[key],
    };
  }).filter((group) => group.documents.length > 0);
};

const DocumentTable: React.FC<{
  documents: ProjectDocumentItem[];
  onReview: (id: string) => void;
  onDownload: (id: string) => void;
}> = ({ documents, onReview, onDownload }) => {
  const { t } = useTranslation();
  return (
    <PagedItems items={documents}>{pageDocuments => <Table size="small" sx={{ minWidth: 760 }}>
      <TableHead>
        <TableRow>
          <TableCell>{t('unfiledPage.fileName')}</TableCell>
          <TableCell>{t('unfiledPage.type')}</TableCell>
          <TableCell>{t('projectDocuments.fields')}</TableCell>
          <TableCell>{t('common.status')}</TableCell>
          <TableCell align="right">{t('common.actions')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {pageDocuments.map((document) => (
          <TableRow
            key={document.documentId}
            hover
            tabIndex={0}
            role="link"
            aria-label={t('projectDocuments.openDocument', { name: document.fileName })}
            onClick={() => onReview(document.documentId!)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onReview(document.documentId!);
              }
            }}
            sx={{ cursor: 'pointer', '&:focus-visible': { outline: 2, outlineColor: 'primary.main', outlineOffset: -2 } }}
          >
            <TableCell sx={{ fontWeight: 600, maxWidth: 360, overflowWrap: 'anywhere' }}>{document.fileName}</TableCell>
            <TableCell>{document.documentTypeName ?? document.documentTypeCode ?? '-'}</TableCell>
            <TableCell>{document.fieldCount ?? 0}</TableCell>
            <TableCell><StatusChip label={document.status ?? ''} tone={documentStatusTone(document.status)} /></TableCell>
            <TableCell align="right">
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                {(document.fieldCount ?? 0) > 0 && (
                  <Button size="md" variant="primary" onClick={(event) => { event.stopPropagation(); onReview(document.documentId!); }}>{t('projectSummary.review')}</Button>
                )}
                <Button size="md" variant="secondary" onClick={(event) => { event.stopPropagation(); onDownload(document.documentId!); }}>{t('projectSummary.downloadFile')}</Button>
              </Stack>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>}</PagedItems>
  );
};

const ExtractedDataPanel: React.FC<{
  summary: ProjectSummaryResponse;
  onReview: (documentId: string) => void;
}> = ({ summary, onReview }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [reviewOnly, setReviewOnly] = useState(false);
  const areas = useMemo(() => {
    const fieldsByDocument = new Map<string | undefined, ProjectFieldItem[]>();
    for (const field of summary.extractedFields ?? []) {
      const entries = fieldsByDocument.get(field.documentId) ?? [];
      entries.push(field);
      fieldsByDocument.set(field.documentId, entries);
    }
    const term = search.trim().toLocaleLowerCase();
    return groupDocuments((summary.documents ?? []).filter(d => fieldsByDocument.has(d.documentId)), '').map(area => ({
      ...area,
      documents: area.documents.map(document => ({ document, fields: (fieldsByDocument.get(document.documentId) ?? [])
        .filter(field => (!reviewOnly || field.status === 'NeedsReview') && (!term || [document.fileName, document.documentTypeName, field.displayLabel, field.fieldKey, field.rawValue, field.status]
          .some(value => value?.toLocaleLowerCase().includes(term)))) })).filter(item => item.fields.length > 0),
    })).filter(area => area.documents.length > 0);
  }, [summary.documents, summary.extractedFields, search, reviewOnly]);
  if (!summary.extractedFields?.length) return <Typography color="text.secondary">{t('projectSummary.noExtracted')}</Typography>;

  return (
    <Stack spacing={2}>
      <Alert severity="info">{t('projectSummary.extractedHint')}</Alert>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ md: 'center' }}>
        <Box>
          <Typography sx={{ fontWeight: 600 }}>{t('extractedData.title')}</Typography>
          <Typography color="text.secondary">{t('extractedData.hint')}</Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('extractedData.search')}
            inputProps={{ 'aria-label': t('extractedData.search') }}
            sx={{ width: { xs: '100%', sm: 320 } }}
          />
          <Button
            size="md"
            variant={reviewOnly ? 'primary' : 'secondary'}
            aria-pressed={reviewOnly}
            onClick={() => setReviewOnly((value) => !value)}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {t('extractedData.reviewOnly')}
          </Button>
        </Stack>
      </Stack>
      {areas.map((area) => (
        <Paper
          key={area.key}
          variant="outlined"
          sx={{ borderRadius: 2, overflow: 'hidden', borderColor: area.border, borderLeft: 4, borderLeftColor: area.accent }}
        >
          <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ px: 2.5, py: 2, bgcolor: area.background }}>
            <Typography sx={{ fontWeight: 600, color: area.heading }}>{t(`projectDocuments.groups.${area.key}`)}</Typography>
            <StatusChip label={t('projectDocuments.count', { count: area.documents.length })} />
            <StatusChip label={t('extractedData.fieldCount', { count: area.documents.reduce((count, item) => count + item.fields.length, 0) })} />
          </Stack>
          <Stack spacing={1.5} sx={{ p: 1.5 }}>
            <PagedItems key={`${search}-${reviewOnly}`} items={area.documents}>{pageDocuments => pageDocuments.map(({ document, fields: documentFields }) => {
              const pending = documentFields.filter((field) => field.status === 'NeedsReview').length;
              const categories = groupFields(documentFields);
              return (
                <Accordion
                  key={document.documentId}
                  TransitionProps={{ timeout: 0, unmountOnExit: true }}
                  disableGutters
                  sx={{ ...instantAccordionSx, border: 1, borderColor: 'divider', borderRadius: '8px !important', '&::before': { display: 'none' } }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, minHeight: 64 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={1} sx={{ width: '100%', pr: 1 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{document.fileName}</Typography>
                        <Typography variant="textSM" color="text.secondary">{document.documentTypeName ?? document.documentTypeCode ?? '-'}</Typography>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                        <StatusChip label={t('extractedData.fieldCount', { count: documentFields.length })} />
                        {pending > 0 && <StatusChip label={t('projectSummary.toReview', { count: pending })} tone="warning" />}
                        <StatusChip label={document.status ?? ''} tone={documentStatusTone(document.status)} />
                      </Stack>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 2, pt: 0, borderTop: 1, borderColor: 'divider' }}>
                    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '240px minmax(0, 1fr)' } }}>
                      <Box sx={{ pt: 2 }}>
                        <Typography variant="textSM" color="text.secondary">{t('extractedData.sourceDocument')}</Typography>
                        <Typography sx={{ fontWeight: 600, mt: 0.5, overflowWrap: 'anywhere' }}>{document.fileName}</Typography>
                        <Button size="md" variant="primary" onClick={() => onReview(document.documentId!)} sx={{ mt: 2 }}>
                          {t('extractedData.openReview')}
                        </Button>
                      </Box>
                      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, pt: 2 }}>
                        {categories.map((category) => (
                          <Paper key={category.key} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                            <Typography sx={{ px: 2, py: 1.25, fontWeight: 600, bgcolor: 'custom.gray.25', borderBottom: 1, borderColor: 'divider' }}>
                              {t(`extractedData.categories.${category.key}`)}
                            </Typography>
                            <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
                              <PagedItems items={category.fields} pageSize={50}>{pageFields => pageFields.map((field) => (
                                <Box key={field.fieldId} sx={{ p: 2 }}>
                                  <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                                    <Typography variant="textSM" color="text.secondary">{field.displayLabel ?? formatFieldKey(field.fieldKey!)}</Typography>
                                    <Stack direction="row" spacing={0.5} flexShrink={0} useFlexGap flexWrap="wrap" justifyContent="flex-end">
                                      <StatusChip label={formatConfidence(field.confidence)} tone={confidenceTone(field.confidence)} />
                                      <StatusChip label={field.status ?? ''} tone={fieldStatusTone(field.status)} />
                                    </Stack>
                                  </Stack>
                                  <Typography sx={{ mt: 0.5, fontWeight: 600, overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>{field.rawValue || '-'}</Typography>
                                </Box>
                              ))}</PagedItems>
                            </Stack>
                          </Paper>
                        ))}
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}</PagedItems>
          </Stack>
        </Paper>
      ))}
      {areas.length === 0 && (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
          <Typography color="text.secondary">{t('extractedData.noMatches')}</Typography>
        </Paper>
      )}
    </Stack>
  );
};

const measurementTerms = ['percent', 'mass', 'weight', 'moisture', 'density', 'size', 'temperature', 'rainfall', 'quantity', 'limit', 'factor', 'angle'];
const identityTerms = ['cargo', 'commodity', 'vessel', 'imo', 'reference', 'client', 'project', 'transport_document'];
const dateTerms = ['date', 'time', 'period', 'month', 'year'];
const methodTerms = ['method', 'standard', 'procedure', 'test_type', 'sampling_tool'];
const partyTerms = ['company', 'shipper', 'consignee', 'carrier', 'surveyor', 'inspector', 'laboratory', 'lab_name', 'port', 'location', 'place'];

const fieldCategory = (field: ProjectFieldItem): string => {
  const key = field.fieldKey?.toLocaleLowerCase() ?? '';
  if (identityTerms.some((term) => key.includes(term))) return 'identity';
  if (measurementTerms.some((term) => key.includes(term))) return 'measurements';
  if (dateTerms.some((term) => key.includes(term))) return 'dates';
  if (methodTerms.some((term) => key.includes(term))) return 'methods';
  if (partyTerms.some((term) => key.includes(term))) return 'parties';
  return 'other';
};

const fieldCategoryOrder = ['identity', 'measurements', 'dates', 'methods', 'parties', 'other'];

const groupFields = (fields: ProjectFieldItem[]) => fieldCategoryOrder.map((key) => ({
  key,
  fields: fields.filter((field) => fieldCategory(field) === key),
})).filter((category) => category.fields.length > 0);

const formatFieldKey = (key: string): string => key.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toLocaleUpperCase());

export default ProjectSummaryPage;
